/**
 * Form data initialization and management
 */

import { loadState } from './localStorage.js';
import { STEPS } from '../steps.js';
import { DEFAULT_MIN_AGE } from '../utils/dateHelpers.js';
import {
    DEFAULT_PASSWORD_MIN_LENGTH,
    DEFAULT_PASSWORD_MAX_LENGTH,
    DEFAULT_PASSWORD_MAX_BYTES,
} from '../validation/validators.js';

/**
 * The geolocation fields after a reload. Coordinates are never stored, so a granted request is
 * marked to run again at init (the browser does not prompt for a granted permission), and any
 * other outcome keeps the recorded status with no position.
 */
export function restoreGeoState(savedState) {
    const status = savedState?.geoPermissionStatus || '';
    if (!savedState?.locationRequested || status === 'granted') {
        return {
            locationRequested: false,
            locationReady: false,
            geoObservation: null,
            geoResumeGranted: Boolean(savedState?.locationRequested) && status === 'granted',
        };
    }
    return {
        locationRequested: true,
        locationReady: true,
        geoObservation: status ? { permission_status: status, lat: null, lon: null, accuracy: null } : null,
        geoResumeGranted: false,
    };
}

export function initializeFormData(serverConfig = {}) {
    const savedState = loadState();
    const geo = restoreGeoState(savedState);

    // Server-provided data takes precedence over localStorage for data values,
    // but currentStep from localStorage is preserved
    const initialStep = savedState?.currentStep || 1;

    const formData = {
        currentStep: initialStep,
        totalSteps: STEPS.length,
        isTransitioning: false,
        isSubmitting: false,
        showContent: false,
        isInitializing: true,

        // Form fields - prioritize saved state, fall back to server config
        name: savedState?.name || serverConfig.name || '',
        email: savedState?.email || serverConfig.email || '',
        day: savedState?.day || (serverConfig.day ? String(serverConfig.day).padStart(2, '0') : ''),
        month: savedState?.month || (serverConfig.month ? String(serverConfig.month).padStart(2, '0') : ''),
        year: savedState?.year || (serverConfig.year ? String(serverConfig.year) : ''),
        sex: savedState?.sex || serverConfig.sex || '',
        username: savedState?.username || serverConfig.username || '',
        password: '', // Never restored from localStorage for security
        cityId: savedState?.cityId || serverConfig.cityId || '',
        cityName: savedState?.cityName || serverConfig.cityName || '',
        cityCountryIso: savedState?.cityCountryIso || serverConfig.cityCountryIso || '',
        homeCityId: savedState?.homeCityId || serverConfig.homeCityId || '',
        homeCityName: savedState?.homeCityName || serverConfig.homeCityName || '',
        homeCityCountryIso: savedState?.homeCityCountryIso || serverConfig.homeCityCountryIso || '',

        // Validation - always start fresh, don't restore from localStorage
        errors: {},
        touched: {},
        // Per-field flag set on first user input. Stays true once flipped.
        // Step inputs gate the `touched` promotion on this so that focus
        // passing through an untouched empty field does not surface an
        // error, but a field that has held content keeps surfacing errors
        // after being cleared.
        dirty: {},
        // The last availability answer per field, keyed by the value it answered.
        availabilityChecks: { email: null, username: null },

        // Geolocation
        locationRequested: geo.locationRequested,
        locationReady: geo.locationReady,
        userLat: null,
        userLon: null,

        // Confirm sheet for the two values that lock when the account is
        // created. No flag is persisted, so after a reload the wizard asks
        // again before it moves past the gender step.
        identityConfirmOpen: false,
        identityConfirmed: false,
        identityConfirmResume: null,

        // One-time pre-permission explainer shown before the OS prompt.
        geoExplainerShown: savedState?.geoExplainerShown || false,
        geoExplainerOpen: false,
        // Captured browser-permission outcome, POSTed after account creation.
        geoObservation: geo.geoObservation,
        // Set when a granted request ran before a reload. init() repeats it without a prompt.
        geoResumeGranted: geo.geoResumeGranted,

        // Minimum registration age, replaced by the server value in init().
        // Never restored from localStorage, which would let a stale policy
        // value outlive a change to the server constant.
        minAge: DEFAULT_MIN_AGE,

        // Password bounds, same arrangement and for the same reason.
        passwordMinLength: DEFAULT_PASSWORD_MIN_LENGTH,
        passwordMaxLength: DEFAULT_PASSWORD_MAX_LENGTH,
        passwordMaxBytes: DEFAULT_PASSWORD_MAX_BYTES
    };

    // Apply server config overrides (except currentStep)
    Object.keys(serverConfig).forEach(key => {
        if (key === 'currentStep') return; // Preserve from localStorage
        if (key === 'errors') {
            // Handle errors specially
            formData.errors = parseErrors(serverConfig.errors);
            return;
        }
        if (
            key === 'minAge'
            || key === 'passwordMinLength'
            || key === 'passwordMaxLength'
            || key === 'passwordMaxBytes'
        ) {
            // Keep the mirrored default when the attribute is absent or
            // unparseable, so a template regression cannot widen the gate.
            // Number('') is 0 and passes isFinite, so zero is rejected too.
            if (Number.isFinite(serverConfig[key]) && serverConfig[key] > 0) {
                formData[key] = serverConfig[key];
            }
            return;
        }
        if (serverConfig[key] !== undefined && serverConfig[key] !== null && serverConfig[key] !== '') {
            formData[key] = serverConfig[key];

            // Ensure proper formatting for date fields
            if (key === 'day' && serverConfig[key]) {
                formData.day = String(serverConfig[key]).padStart(2, '0');
            }
            if (key === 'month' && serverConfig[key]) {
                formData.month = String(serverConfig[key]).padStart(2, '0');
            }
            if (key === 'year' && serverConfig[key]) {
                formData.year = String(serverConfig[key]);
            }
        }
    });

    return formData;
}

function parseErrors(errors) {
    if (!errors) return {};

    try {
        if (typeof errors === 'string') {
            return JSON.parse(errors);
        }
        return errors;
    } catch (e) {
        console.error('[Signup] Error parsing errors:', e);
        return {};
    }
}
